import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { DataLayerService } from '../analytics/data-layer.service';
import type {
  PasswordPopupDismissMethod,
  PasswordPopupTriggerContext,
} from '../analytics/data-layer.types';
import { clientErrorMessage, extractBackendMessages } from '../http/client-error-message';

import { AuthService } from './auth.service';

/**
 * Global coordinator for Luxury's second-step password validation.
 *
 * DNI/document login creates a `guest` session. Cart/checkout/private flows call
 * `ensureFullAuth()`; if the session is still guest this service opens the
 * modal and resolves the pending action only after `/auth/validate-password`
 * upgrades the cookies to `authLevel=full`.
 */
@Injectable({ providedIn: 'root' })
export class RequireFullAuthService {
  private readonly auth = inject(AuthService);
  private readonly dataLayer = inject(DataLayerService);
  private readonly pendingResolvers = new Set<(allowed: boolean) => void>();
  private triggerContext: PasswordPopupTriggerContext = 'item_list';
  private openedAt = 0;

  readonly open = signal(false);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  ensureFullAuth(
    options: { force?: boolean; triggerContext?: PasswordPopupTriggerContext } = {},
  ): Promise<boolean> {
    if (!options.force && this.auth.isFullyAuthenticated()) {
      return Promise.resolve(true);
    }
    if (!this.auth.isAuthenticated()) {
      return Promise.resolve(false);
    }

    this.error.set(null);
    if (!this.open()) {
      this.triggerContext = options.triggerContext ?? this.dataLayer.passwordPopupContext();
      this.openedAt = Date.now();
      this.dataLayer.passwordPopupTrigger(this.triggerContext);
    }
    this.open.set(true);

    return new Promise<boolean>((resolve) => {
      this.pendingResolvers.add(resolve);
    });
  }

  isFullAuthRequiredError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || (error.status !== 401 && error.status !== 403)) {
      return false;
    }
    return extractBackendMessages(error.error).some((message) =>
      message.toLocaleLowerCase('es-PE').includes('contraseña'),
    );
  }

  async confirm(password: string, remember: boolean): Promise<void> {
    if (!password) {
      this.error.set('Ingresa la contraseña para continuar.');
      return;
    }
    if (this.loading()) return;

    this.loading.set(true);
    this.error.set(null);
    try {
      await this.auth.validatePassword(password, remember);
      this.settle(true);
    } catch (error) {
      this.error.set(this.messageFromError(error));
    } finally {
      this.loading.set(false);
    }
  }

  cancel(dismissMethod: PasswordPopupDismissMethod = 'close_button'): void {
    if (this.loading()) return;
    this.settle(false, dismissMethod);
  }

  private settle(
    allowed: boolean,
    dismissMethod: PasswordPopupDismissMethod = 'close_button',
  ): void {
    if (this.open()) {
      if (allowed) {
        this.dataLayer.passwordPopupSuccess(
          this.triggerContext,
          (Date.now() - this.openedAt) / 1000,
        );
      } else {
        this.dataLayer.passwordPopupDismiss(this.triggerContext, dismissMethod);
      }
    }
    this.open.set(false);
    this.error.set(null);
    const resolvers = Array.from(this.pendingResolvers);
    this.pendingResolvers.clear();
    resolvers.forEach((resolve) => resolve(allowed));
  }

  private messageFromError(error: unknown): string {
    return clientErrorMessage(error, 'No pudimos validar tu contraseña. Inténtalo nuevamente.');
  }
}
